import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Move, Plus, Trash2, Save, Edit3, Eye, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const STATUS_BG = {
  libre: 'bg-[hsl(var(--card))] border-[hsl(var(--border))]',
  ocupada: 'bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]',
  cuenta_solicitada: 'bg-[hsl(43_92%_55%)] text-white border-[hsl(43_92%_55%)]',
  reservada: 'bg-[hsl(199_70%_55%)] text-white border-[hsl(199_70%_55%)]',
};

// Limiar em pixels: movimento abaixo disso é tratado como CLIQUE, não drag.
const DRAG_THRESHOLD = 6;

export default function FloorPlan() {
  const { t, lang } = useI18n();
  const [tables, setTables] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [activeZone, setActiveZone] = useState('Salón');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // table object pendente delete
  const containerRef = useRef(null);
  const draggingRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/tables');
      setTables(r.data);
      const newZones = [...new Set(r.data.map(tt => tt.zona).filter(Boolean))];
      setActiveZone(prev => (newZones.length && !newZones.includes(prev)) ? newZones[0] : prev);
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e?.message));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const zones = useMemo(
    () => [...new Set([...tables.map(tt => tt.zona).filter(Boolean), 'Salón'])],
    [tables],
  );
  const filtered = useMemo(
    () => tables.filter(tt => (tt.zona || 'Salón') === activeZone),
    [tables, activeZone],
  );

  // ============ Pointer handling: distinguish CLICK vs DRAG ============
  const onPointerDown = (e, table) => {
    if (!editMode) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const tileRect = e.currentTarget.getBoundingClientRect();
    draggingRef.current = {
      id: table.id,
      table,
      offsetX: e.clientX - tileRect.left,
      offsetY: e.clientY - tileRect.top,
      containerRect: rect,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    // Não usar setPointerCapture: em alguns browsers (Edge, mobile Safari)
    // a captura do pointer impede o onPointerUp/onClick subsequente de
    // disparar normalmente, fazendo o clique no modo edição "sumir".
  };

  const onPointerMove = (e) => {
    if (!editMode || !draggingRef.current) return;
    const d = draggingRef.current;
    const dx = Math.abs(e.clientX - d.startX);
    const dy = Math.abs(e.clientY - d.startY);
    if (!d.moved && (dx + dy < DRAG_THRESHOLD)) return; // ainda é "click"
    d.moved = true;
    const x = e.clientX - d.containerRect.left - d.offsetX;
    const y = e.clientY - d.containerRect.top - d.offsetY;
    const cx = Math.max(0, Math.min(d.containerRect.width - 80, x));
    const cy = Math.max(0, Math.min(d.containerRect.height - 80, y));
    setTables(prev => prev.map(tt =>
      tt.id === d.id ? { ...tt, posicion_x: Math.round(cx), posicion_y: Math.round(cy) } : tt
    ));
  };

  const onPointerUp = async (e, table) => {
    if (!editMode) return;
    const d = draggingRef.current;
    draggingRef.current = null;
    if (!d) return;
    if (d.moved) {
      // Foi drag — persiste a nova posição
      const cur = tables.find(tt => tt.id === d.id);
      if (cur) {
        try {
          await api.put(`/tables/${cur.id}/position`, {
            posicion_x: cur.posicion_x,
            posicion_y: cur.posicion_y,
            zona: cur.zona,
          });
        } catch (err) {
          toast.error('Erro ao mover: ' + (err?.response?.data?.detail || err?.message));
          load(); // reverte UI
        }
      }
    } else {
      // Foi clique sem arraste → abre dialog de edição
      setSelectedTable(table);
    }
  };

  const onPointerCancel = () => {
    draggingRef.current = null;
  };

  const onTileClick = (table) => {
    // Só dispara em modo NÃO-edição (modo serviço → vai pro POS).
    // No modo edição o pointerUp já abre o dialog.
    if (!editMode) {
      window.location.assign('/pos');
    }
  };

  // ============ Texto traduzido (com fallback inline pra strings que
  // não têm chave i18n) ============
  const txt = {
    edit: lang === 'pt' ? 'Editar' : 'Editar',
    finish: lang === 'pt' ? 'Concluir' : 'Terminar',
    editHint: lang === 'pt'
      ? 'Modo edição: arraste as mesas para posicionar. Clique numa mesa para editar/apagar.'
      : 'Modo edición: arrastra las mesas para colocarlas. Click en una mesa para editar/borrar.',
    noTables: lang === 'pt'
      ? 'Sem mesas nesta zona. Crie a primeira ou troque de zona.'
      : 'Sin mesas en esta zona. Crea la primera o cambia de zona.',
    confirmDeleteTitle: lang === 'pt' ? 'Apagar mesa?' : '¿Borrar mesa?',
    confirmDeleteBody: lang === 'pt'
      ? 'Esta ação não pode ser desfeita. A mesa será removida do plano.'
      : 'Esta acción no se puede deshacer. La mesa se eliminará del plano.',
    yesDelete: lang === 'pt' ? 'Sim, apagar' : 'Sí, borrar',
    no: lang === 'pt' ? 'Não' : 'No',
    optional: lang === 'pt' ? 'opcional' : 'opcional',
    tableTitle: lang === 'pt' ? 'Mesa' : 'Mesa',
    deleted: lang === 'pt' ? 'Mesa apagada' : 'Mesa borrada',
    saved: lang === 'pt' ? 'Salvo' : 'Guardado',
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid="floor-plan-page">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b bg-[hsl(var(--card))]">
        <div className="font-heading font-semibold text-lg flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-[hsl(var(--primary))]" />
          {t('nav_floor_plan')}
        </div>
        <div className="flex gap-1 ml-auto">
          {zones.map(z => (
            <Button
              key={z}
              variant={activeZone === z ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveZone(z)}
              data-testid={`floor-zone-${z}`}
            >
              {z}
            </Button>
          ))}
          <Button
            variant={editMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setEditMode(m => !m)}
            className="ml-2"
            data-testid="floor-edit-toggle"
          >
            {editMode ? <Save className="h-3.5 w-3.5 mr-1" /> : <Edit3 className="h-3.5 w-3.5 mr-1" />}
            {editMode ? txt.finish : txt.edit}
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="floor-create-table-button">
            <Plus className="h-3.5 w-3.5 mr-1" /> {t('create_table')}
          </Button>
        </div>
      </div>

      <div className="px-4 py-2 text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))]"></span>
          {t('table_status_libre')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--secondary))]"></span>
          {t('table_status_ocupada')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(43_92%_55%)]"></span>
          {t('table_status_cuenta_solicitada')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(199_70%_55%)]"></span>
          {t('table_status_reservada')}
        </span>
        {editMode && <span className="ml-auto italic">{txt.editHint}</span>}
      </div>

      <div
        ref={containerRef}
        className={cn(
          'flex-1 relative overflow-auto bg-[hsl(var(--muted))]/30',
          editMode ? 'cursor-crosshair' : ''
        )}
        style={{
          backgroundImage: editMode ? 'radial-gradient(hsl(var(--border)) 1px, transparent 1px)' : 'none',
          backgroundSize: editMode ? '20px 20px' : 'auto',
        }}
        onPointerMove={onPointerMove}
      >
        {filtered.length === 0 ? (
          <div className="absolute inset-0 grid place-items-center text-center">
            <div>
              <LayoutGrid className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
                {txt.noTables}
              </p>
              <Button onClick={() => setCreateOpen(true)} size="sm">
                <Plus className="h-3 w-3 mr-1" /> {t('create_table')}
              </Button>
            </div>
          </div>
        ) : filtered.map(table => (
          <button
            key={table.id}
            onPointerDown={(e) => onPointerDown(e, table)}
            onPointerUp={(e) => onPointerUp(e, table)}
            onPointerCancel={onPointerCancel}
            onClick={() => onTileClick(table)}
            data-testid={`floor-table-${table.numero}`}
            className={cn(
              'absolute h-20 w-20 rounded-2xl border-2 grid place-items-center font-heading font-bold transition-shadow',
              STATUS_BG[table.estado] || STATUS_BG.libre,
              editMode ? 'cursor-grab active:cursor-grabbing shadow-md' : 'hover:shadow-lg cursor-pointer',
              draggingRef.current?.id === table.id && 'shadow-2xl scale-105 z-10',
            )}
            style={{
              left: `${table.posicion_x || 20}px`,
              top: `${table.posicion_y || 20}px`,
              touchAction: 'none',
            }}
          >
            <div className="text-2xl leading-none">{table.numero}</div>
            <div className="text-[10px] mt-0.5 opacity-80">{table.capacidad}p</div>
          </button>
        ))}
      </div>

      <CreateTableDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        zones={zones}
        defaultZone={activeZone}
        t={t}
        lang={lang}
        txt={txt}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
      <EditTableDialog
        open={!!selectedTable}
        onOpenChange={(o) => !o && setSelectedTable(null)}
        table={selectedTable}
        zones={zones}
        t={t}
        lang={lang}
        txt={txt}
        onSaved={() => { setSelectedTable(null); load(); }}
        onAskDelete={(tb) => { setSelectedTable(null); setConfirmDelete(tb); }}
      />
      <ConfirmDeleteDialog
        table={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        txt={txt}
        onConfirmed={async () => {
          if (!confirmDelete) return;
          try {
            await api.delete(`/tables/${confirmDelete.id}`);
            toast.success(txt.deleted);
            setConfirmDelete(null);
            load();
          } catch (err) {
            toast.error(
              'Erro: ' + (err?.response?.data?.detail || err?.response?.statusText || err?.message)
            );
          }
        }}
      />
    </div>
  );
}

// ====================================================================
function CreateTableDialog({ open, onOpenChange, zones, defaultZone, t, lang, txt, onCreated }) {
  const [form, setForm] = useState({
    numero: '', nombre: '', capacidad: 4, zona: defaultZone, posicion_x: 40, posicion_y: 40
  });
  useEffect(() => { setForm(f => ({ ...f, zona: defaultZone })); }, [defaultZone, open]);

  const save = async () => {
    try {
      await api.post('/tables', form);
      toast.success(txt.saved);
      onCreated();
    } catch (err) {
      toast.error('Erro: ' + (err?.response?.data?.detail || err?.message));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="floor-create-dialog">
        <DialogHeader><DialogTitle>{t('create_table')}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t('table_number')}</Label>
            <Input value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} data-testid="floor-create-number" />
          </div>
          <div>
            <Label>{t('capacity')}</Label>
            <Input type="number" min={1} value={form.capacidad} onChange={e => setForm({ ...form, capacidad: Number(e.target.value) })} />
          </div>
          <div className="col-span-2">
            <Label>{t('name')}</Label>
            <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder={txt.optional} />
          </div>
          <div className="col-span-2">
            <Label>{t('zone')}</Label>
            <Select value={form.zona} onValueChange={v => setForm({ ...form, zona: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                <SelectItem value="Salón">Salón</SelectItem>
                <SelectItem value="Terraza">Terraza</SelectItem>
                <SelectItem value="Barra">Barra</SelectItem>
                <SelectItem value="Privado">Privado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!form.numero} data-testid="floor-create-save">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====================================================================
function EditTableDialog({ open, onOpenChange, table, zones, t, lang, txt, onSaved, onAskDelete }) {
  const [form, setForm] = useState({ numero: '', nombre: '', capacidad: 4, zona: 'Salón', posicion_x: 0, posicion_y: 0 });

  useEffect(() => {
    if (table) setForm({
      numero: table.numero || '',
      nombre: table.nombre || '',
      capacidad: table.capacidad || 4,
      zona: table.zona || 'Salón',
      posicion_x: table.posicion_x || 0,
      posicion_y: table.posicion_y || 0,
    });
  }, [table, open]);

  if (!table) return null;

  const save = async () => {
    try {
      await api.put(`/tables/${table.id}`, form);
      toast.success(txt.saved);
      onSaved();
    } catch (err) {
      toast.error('Erro: ' + (err?.response?.data?.detail || err?.response?.statusText || err?.message));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="floor-edit-dialog">
        <DialogHeader>
          <DialogTitle>{txt.tableTitle} {table.numero}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t('table_number')}</Label>
            <Input value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} />
          </div>
          <div>
            <Label>{t('capacity')}</Label>
            <Input type="number" min={1} value={form.capacidad} onChange={e => setForm({ ...form, capacidad: Number(e.target.value) })} />
          </div>
          <div className="col-span-2">
            <Label>{t('name')}</Label>
            <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder={txt.optional} />
          </div>
          <div className="col-span-2">
            <Label>{t('zone')}</Label>
            <Select value={form.zona} onValueChange={v => setForm({ ...form, zona: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Salón">Salón</SelectItem>
                <SelectItem value="Terraza">Terraza</SelectItem>
                <SelectItem value="Barra">Barra</SelectItem>
                <SelectItem value="Privado">Privado</SelectItem>
                {zones.filter(z => !['Salón', 'Terraza', 'Barra', 'Privado'].includes(z)).map(z =>
                  <SelectItem key={z} value={z}>{z}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>X (px)</Label>
            <Input type="number" value={form.posicion_x} onChange={e => setForm({ ...form, posicion_x: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Y (px)</Label>
            <Input type="number" value={form.posicion_y} onChange={e => setForm({ ...form, posicion_y: Number(e.target.value) })} />
          </div>
        </div>
        <DialogFooter className="gap-2 flex flex-col-reverse sm:flex-row sm:justify-between">
          <Button
            variant="destructive"
            onClick={() => onAskDelete(table)}
            data-testid="floor-edit-delete"
          >
            <Trash2 className="h-4 w-4 mr-1" /> {t('delete')}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
            <Button onClick={save} data-testid="floor-edit-save">{t('save')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====================================================================
function ConfirmDeleteDialog({ table, onCancel, onConfirmed, txt }) {
  return (
    <Dialog open={!!table} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="floor-delete-confirm">
        <DialogHeader>
          <DialogTitle>{txt.confirmDeleteTitle}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          {txt.confirmDeleteBody}
        </p>
        {table && (
          <p className="font-mono text-sm">
            #{table.numero} — {table.nombre || '(sem nome)'} — {table.capacidad}p
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{txt.no}</Button>
          <Button variant="destructive" onClick={onConfirmed} data-testid="floor-delete-confirm-yes">
            <Trash2 className="h-4 w-4 mr-1" /> {txt.yesDelete}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
